import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../i18n';
import { fetchAllVideos, type VideoMeta } from '../youtube';
import { PLAYTHROUGH_TABS } from '../config';
import VideoCard from '../components/VideoCard';

interface OrderMap {
  char: Record<string, string[]>;
  sport: Record<string, string[]>;
  doom: Record<string, string[]>;
}

let orderMapCache: OrderMap | null = null;
async function loadOrderMap(): Promise<OrderMap> {
  if (orderMapCache) return orderMapCache;
  try {
    // 'no-cache' = always revalidate with the server (no stale copy).
    // We then cache the parsed object at module scope for the rest of the
    // session so we don't refetch on every navigation.
    const res = await fetch('/data/video-order.json', { cache: 'no-cache' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    orderMapCache = {
      char: data.char ?? {},
      sport: data.sport ?? {},
      doom: data.doom ?? {},
    };
  } catch {
    orderMapCache = { char: {}, sport: {}, doom: {} };
  }
  return orderMapCache;
}

// Top-level category strip.
const MAIN_TABS = [
  'playthrough',
  'charPlaythrough',
  'sportPlaythrough',
  'doomPlaythrough',
  'superplay',
  'compilation',
] as const;
type MainTab = (typeof MAIN_TABS)[number];

// Hardcoded sub-tabs for Doom (only three platforms ever).
const DOOM_SUBS = [
  { id: 'doom-playthrough-super-nes', label: 'Doom (Super NES)' },
  { id: 'doom-playthrough-sega-saturn', label: 'Doom (Sega Saturn)' },
  { id: 'doom-playthrough-sega-32x', label: 'Doom (Sega 32X)' },
] as const;

interface SubTab {
  id: string;
  label: string;
}

export default function Home() {
  const { t } = useLanguage();
  const [videos, setVideos] = useState<VideoMeta[] | null>(null);
  const [orderMap, setOrderMap] = useState<OrderMap | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [mainTab, setMainTab] = useState<MainTab>('playthrough');
  // 'all' means "show every video that fits the current main tab". Otherwise
  // it's a specific sub-category id (e.g. char-playthrough-mortal-kombat-super-nes).
  const [subTab, setSubTab] = useState<string>('playthrough');

  useEffect(() => {
    let cancelled = false;
    fetchAllVideos()
      .then((v) => { if (!cancelled) setVideos(v); })
      .catch((e: Error) => { if (!cancelled) setError(e.message); });
    loadOrderMap().then((m) => { if (!cancelled) setOrderMap(m); });
    return () => { cancelled = true; };
  }, []);

  // Reset the sub-tab whenever the main category changes — each main tab
  // has its own meaningful default.
  const onMainTabChange = (id: MainTab) => {
    setMainTab(id);
    if (id === 'playthrough') setSubTab('playthrough');
    else if (id === 'charPlaythrough') setSubTab('char-playthrough');
    else if (id === 'sportPlaythrough') setSubTab('sport-playthrough');
    else if (id === 'doomPlaythrough') setSubTab('doom-playthrough');
    else setSubTab(id);
  };

  // --- Sub-tab lists derived from current video set -----------------
  // For char and sport, we discover distinct sub-categories by scanning
  // the loaded videos. Each unique `category` becomes a tab; the human
  // label comes from `charLabel` (set by detectCategory).
  const charSubs: SubTab[] = useMemo(() => {
    if (!videos) return [];
    const seen = new Map<string, string>();
    for (const v of videos) {
      if (!v.category.startsWith('char-playthrough-')) continue;
      if (!seen.has(v.category)) {
        seen.set(v.category, v.charLabel ?? v.category);
      }
    }
    return [...seen.entries()]
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [videos]);

  const sportSubs: SubTab[] = useMemo(() => {
    if (!videos) return [];
    const seen = new Map<string, string>();
    for (const v of videos) {
      if (!v.category.startsWith('sport-playthrough-')) continue;
      if (!seen.has(v.category)) {
        seen.set(v.category, v.charLabel ?? v.category);
      }
    }
    return [...seen.entries()]
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [videos]);

  // --- Filtered + sorted list for the current selection -------------
  // Mirrors the original site: if a specific sub-tab is selected, show only
  // matching videos; otherwise (the "all of this main tab" pseudo-id) show
  // every video under that main category. When the sub-tab has a curated
  // ordering in /data/video-order.json, that ordering wins over alphabetic.
  const visible = useMemo(() => {
    if (!videos) return [] as VideoMeta[];

    const filtered = videos.filter((v) => {
      if (subTab === 'playthrough') {
        return v.category === 'playthrough' || v.category.startsWith('playthrough-');
      }
      if (subTab === 'char-playthrough') {
        return v.category.startsWith('char-playthrough-');
      }
      if (subTab === 'sport-playthrough') {
        return v.category === 'sport-playthrough' || v.category.startsWith('sport-playthrough-');
      }
      if (subTab === 'doom-playthrough') {
        return v.category.startsWith('doom-playthrough-');
      }
      return v.category === subTab;
    });

    // Curated order for the selected sub-tab (only for series-specific tabs).
    let curated: string[] | undefined;
    if (orderMap) {
      if (subTab.startsWith('char-playthrough-')) curated = orderMap.char[subTab];
      else if (subTab.startsWith('sport-playthrough-')) curated = orderMap.sport[subTab];
      else if (subTab.startsWith('doom-playthrough-')) curated = orderMap.doom[subTab];
    }
    const hasCurated = !!curated && curated.length > 0;

    return filtered.sort((a, b) => {
      if (hasCurated) {
        const ai = curated!.indexOf(a.id);
        const bi = curated!.indexOf(b.id);
        // -1 (not in list) becomes Infinity so it falls to the tail.
        const aPos = ai === -1 ? Infinity : ai;
        const bPos = bi === -1 ? Infinity : bi;
        if (aPos !== bPos) return aPos - bPos;
      }
      return a.title.localeCompare(b.title);
    });
  }, [videos, subTab, orderMap]);

  if (error) {
    return (
      <div className="error">
        <h2>{t('error')}</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!videos) {
    return <p>{t('loading')}</p>;
  }

  // Pick which sub-tab strip (if any) to render for the current main tab.
  let subTabsToShow: SubTab[] = [];
  let subTabAllId = '';
  let subTabAllLabel = '';
  if (mainTab === 'playthrough') {
    subTabsToShow = [...PLAYTHROUGH_TABS];
    subTabAllId = 'playthrough';
    subTabAllLabel = t('playthroughSubs', 'playthrough');
  } else if (mainTab === 'charPlaythrough') {
    subTabsToShow = charSubs;
    subTabAllId = 'char-playthrough';
    subTabAllLabel = t('allCharPlaythroughs');
  } else if (mainTab === 'sportPlaythrough') {
    subTabsToShow = sportSubs;
    subTabAllId = 'sport-playthrough';
    subTabAllLabel = t('allSportPlaythroughs');
  } else if (mainTab === 'doomPlaythrough') {
    subTabsToShow = [...DOOM_SUBS];
    subTabAllId = 'doom-playthrough';
    subTabAllLabel = t('categories', 'doomPlaythrough');
  }

  // Description shown under the sub-tabs. Prefer a series-specific label
  // (the sub-tab's own label) when one is selected; otherwise the broad
  // i18n description for the main category.
  const sub = subTabsToShow.find((s) => s.id === subTab);
  const description =
    sub?.label ??
    t('categoryDescriptions', subTab === subTabAllId ? mainTab : subTab);

  const hasSubTabs = subTabsToShow.length > 0;

  return (
    <div className="home">
      <Helmet>
        <title>RetroGameUp — {t('siteSubtitle')}</title>
      </Helmet>

      <section className="hero">
        <h1>{t('siteTitle')}</h1>
        <p>{t('siteSubtitle')}</p>
      </section>

      {/* Main category strip. */}
      <nav className="category-tabs" aria-label="Categories">
        {MAIN_TABS.map((id) => (
          <button
            key={id}
            type="button"
            className={`category-button main-category${mainTab === id ? ' active' : ''}`}
            onClick={() => onMainTabChange(id)}
          >
            {t('categories', id)}
          </button>
        ))}
      </nav>

      {/* Sub-tab strip — only shown for the four mains with sub-categories. */}
      {hasSubTabs && (
        <nav className="sub-category-tabs" aria-label="Sub-category">
          <button
            type="button"
            className={`category-button sub-category${subTab === subTabAllId ? ' active' : ''}`}
            onClick={() => setSubTab(subTabAllId)}
          >
            {subTabAllLabel}
          </button>
          {subTabsToShow.map((b) => (
            <button
              key={b.id}
              type="button"
              className={`category-button sub-category${subTab === b.id ? ' active' : ''}`}
              onClick={() => setSubTab(b.id)}
            >
              {b.label}
            </button>
          ))}
        </nav>
      )}

      {description && description !== mainTab && description !== subTab && (
        <p className="category-description">{description}</p>
      )}

      {visible.length > 0 ? (
        <div className="video-grid">
          {visible.map((v) => <VideoCard key={v.id} video={v} />)}
        </div>
      ) : (
        <p>{t('noVideos')} {sub?.label ?? t('categories', mainTab)}.</p>
      )}
    </div>
  );
}
